/*
 * Accessory suggestions inside the cart.
 *
 * The card keeps the colour choice local: picking a swatch only swaps the
 * variant this card would add. Nothing is sent until the add button is used.
 */

/*
 * GA4 list tracking for the recommendations (docs/ga4-empfehlungen.md).
 *
 * Cards carrying data-list-id report view_item_list once at least half of
 * the card is on screen, and select_item when the customer picks them. Both
 * go to the page dataLayer, GTM forwards them to GA4. The purchase side is
 * covered by the _rec_list line item property and the custom pixel.
 */
const recommendationTracking = {
  threshold: 0.5,
  flushDelay: 250,

  // "list_id|item_id" pairs reported during this page view. Cards rendered
  // again after a quantity change must not count a second time.
  seen: new Set(),
  queue: [],
  flushTimer: null,
  observer: null,

  observe(card) {
    if (!('IntersectionObserver' in window)) return;

    if (!this.observer) {
      this.observer = new IntersectionObserver(this.onIntersect.bind(this), { threshold: this.threshold });
    }
    this.observer.observe(card);
  },

  unobserve(card) {
    if (this.observer) this.observer.unobserve(card);
  },

  onIntersect(entries) {
    entries.forEach((entry) => {
      // The first callback after observe() fires for every target, so the
      // ratio is checked too. The small margin absorbs browsers reporting a
      // crossing of 0.5 as 0.499.
      if (!entry.isIntersecting || entry.intersectionRatio < this.threshold - 0.01) return;

      const card = entry.target;
      this.unobserve(card);

      const item = this.itemFor(card);
      const key = `${card.dataset.listId}|${item.item_id}`;
      if (this.seen.has(key)) return;

      this.seen.add(key);
      this.queue.push(item);
    });

    if (this.queue.length === 0) return;
    clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => this.flush(), this.flushDelay);
  },

  /* One view_item_list per list with every card that came into view. */
  flush() {
    clearTimeout(this.flushTimer);
    this.flushTimer = null;

    const lists = new Map();
    this.queue.forEach((item) => {
      if (!lists.has(item.item_list_id)) lists.set(item.item_list_id, []);
      lists.get(item.item_list_id).push(item);
    });
    this.queue = [];

    lists.forEach((items, listId) => this.push('view_item_list', listId, items));
  },

  select(card) {
    // A pending view goes out first to keep the order view, then click.
    if (this.queue.length > 0) this.flush();

    const item = this.itemFor(card);
    this.push('select_item', item.item_list_id, [item]);
  },

  push(event, listId, items) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event,
      ecommerce: { item_list_id: listId, item_list_name: listId, items },
    });
  },

  /* item_id must match the custom pixel's purchase items exactly. */
  itemFor(card) {
    const { listId, productId, variantId, itemName, variantTitle, price, index } = card.dataset;

    return {
      item_id: `shopify_ZZ_${productId}_${variantId}`,
      item_name: itemName,
      item_variant: variantTitle,
      price: Number(price),
      index: Number(index),
      item_list_id: listId,
      item_list_name: listId,
    };
  },
};

class CartAddonCard extends HTMLElement {
  connectedCallback() {
    // Tracking comes before the button check: link cards have no add button
    // but are still recommendations.
    if (this.dataset.listId) recommendationTracking.observe(this);

    if (this.initialised) return;
    this.initialised = true;

    if (this.dataset.listId) this.addEventListener('click', this.onLinkClick.bind(this));

    this.button = this.querySelector('[data-addon-add]');
    if (!this.button) return;

    this.priceElement = this.querySelector('[data-addon-price]');
    this.imageElement = this.querySelector('[data-addon-image]');
    this.valueElement = this.querySelector('[data-addon-option-value]');
    this.errorElement = this.querySelector('[data-addon-error]');

    const variantData = this.querySelector('[data-addon-variants]');
    this.variants = variantData ? JSON.parse(variantData.textContent) : [];

    this.addEventListener('change', this.onOptionChange.bind(this));
    this.button.addEventListener('click', this.onAdd.bind(this));
  }

  disconnectedCallback() {
    // Re-rendered cards are new elements, the observer must let go of the old.
    recommendationTracking.unobserve(this);
  }

  /* Title, image and "choose options" all lead to the product page. */
  onLinkClick(event) {
    if (event.target.closest('a[href]')) recommendationTracking.select(this);
  }

  onOptionChange(event) {
    const input = event.target;
    if (!input.classList.contains('cart-addon__option-input')) return;

    const variant = this.variants.find((entry) => entry.value === input.value);
    if (!variant) return;

    this.dataset.variantId = variant.id;
    this.dataset.variantTitle = variant.title;
    this.dataset.price = variant.amount;
    this.classList.remove('is-added');
    this.setError('');

    if (this.priceElement) this.priceElement.textContent = variant.price;
    if (this.valueElement) this.valueElement.textContent = variant.value;
    if (this.imageElement && variant.image) this.imageElement.src = variant.image;
  }

  onAdd() {
    const variantId = this.dataset.variantId;
    if (!variantId || this.getAttribute('aria-busy') === 'true') return;

    this.setAttribute('aria-busy', 'true');
    this.setError('');

    const drawer = this.closest('cart-drawer');
    const sections = drawer ? drawer.getSectionsToRender() : this.getSectionsToRender();
    const listId = this.dataset.listId;

    const item = { id: Number(variantId), quantity: 1 };
    // Hidden property: travels with the order, so the pixel can name the list
    // that sold the item on the purchase event.
    if (listId) item.properties = { _rec_list: listId };

    const body = JSON.stringify({
      items: [item],
      sections: sections.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.setError(response.description || window.cartStrings.error);
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'cart-addons',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          return;
        }

        this.classList.add('is-added');

        // Before rendering: the new markup replaces this card.
        if (listId) recommendationTracking.select(this);

        if (drawer) {
          drawer.renderContents(response);
        } else {
          this.renderSections(sections, response.sections);
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-addons',
          productVariantId: variantId,
          cartData: response,
        });
      })
      .catch((error) => {
        console.error(error);
        this.setError(window.cartStrings.error);
      })
      .finally(() => {
        this.removeAttribute('aria-busy');
      });
  }

  /* Sections rendered on the cart page. The drawer brings its own list. */
  getSectionsToRender() {
    const sections = [];
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');

    if (items) sections.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    if (footer) sections.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });

    sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    sections.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });

    return sections;
  }

  renderSections(sections, rendered) {
    if (!rendered) return;

    sections.forEach((section) => {
      const container = document.getElementById(section.id);
      const markup = rendered[section.section];
      if (!container || !markup) return;

      const target = container.querySelector(section.selector) || container;
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const source = parsed.querySelector(section.selector);
      if (source) target.innerHTML = source.innerHTML;
    });
  }

  setError(message) {
    if (this.errorElement) this.errorElement.textContent = message;
  }
}

if (!customElements.get('cart-addon-card')) {
  customElements.define('cart-addon-card', CartAddonCard);
}
